import type { GuideMeta, GuideCategoryId } from './types';
import type { Source } from '@/components/editorial/AuthorByline';

/**
 * Generated guide documents.
 *
 * Guides produced by `scripts/generate-guide.ts` land in `generated/*.json` and
 * are DISCOVERED here rather than appended to registry.ts. Discovery beats
 * codegen for two reasons: the script never has to rewrite a TypeScript source
 * file (no fragile AST surgery, no merge conflicts), and the route list can't
 * drift from the content — prerender.mjs reads the same registry the site does.
 *
 * DRAFT GATE: a generated guide is `status: "draft"` until a human publishes it.
 * Drafts are excluded from the registry entirely, so they get no route, no
 * prerendered page, and no sitemap entry. See docs/content-pipeline.md.
 */

export type BlockType =
  | 'lead'
  | 'keyTakeaways'
  | 'heading'
  | 'paragraph'
  | 'list'
  | 'callout'
  | 'table'
  | 'steps'
  | 'checklist'
  | 'communityCta';

export type GuideBlock =
  | { type: 'lead'; text: string }
  | { type: 'keyTakeaways'; items: string[] }
  | { type: 'heading'; level: 2 | 3; id: string; text: string; textKo?: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered?: boolean; items: string[] }
  | {
      type: 'callout';
      variant: 'info' | 'tip' | 'warning' | 'legal' | 'language';
      title: string;
      titleKo: string;
      body: string[];
      bodyKo?: string[];
    }
  | {
      type: 'table';
      caption: string;
      captionKo: string;
      columns: { key: string; header: string; headerKo?: string; numeric?: boolean }[];
      rows: {
        label: string;
        labelKo?: string;
        note?: string;
        highlight?: boolean;
        highlightLabel?: string;
        cells: { key: string; value: string }[];
      }[];
      footnote?: string;
    }
  | {
      type: 'steps';
      items: {
        title: string;
        titleKo?: string;
        body: string[];
        meta?: { label: string; value: string }[];
      }[];
    }
  | {
      type: 'checklist';
      title: string;
      titleKo: string;
      items: { id: string; label: string; labelKo?: string; detail?: string; optional?: boolean }[];
    }
  | { type: 'communityCta'; title: string; titleKo: string; body: string; prompt: string };

/** The model-authored portion — exactly the shape guide-schema.json constrains. */
export interface GeneratedGuideContent {
  title: string;
  titleKo: string;
  shortTitle: string;
  description: string;
  excerpt: string;
  keywords: string[];
  blocks: GuideBlock[];
  faq: { question: string; answer: string }[];
  sources: Source[];
}

/** The full on-disk document: model content plus pipeline-controlled fields. */
export interface GeneratedGuideDocument extends GeneratedGuideContent {
  schemaVersion: 1;
  /** Draft guides are excluded from the registry, routes, prerender and sitemap. */
  status: 'draft' | 'published';
  slug: string;
  category: GuideCategoryId;
  /** Computed from the rendered word count — never model-supplied. */
  readingTimeMinutes: number;
  publishedAt: string;
  updatedAt: string;
  lastVerifiedAt: string;
  authorId: string;
  /**
   * Always false on generation. A human sets this true only after checking the
   * figures — the badge is a claim about a person's work, not the model's.
   */
  factChecked: boolean;
  gradient: string;
  disclaimer?: string;
  related: string[];
  generation: {
    model: string;
    generatedAt: string;
    promptVersion: string;
    topic: string;
  };
}

/* ────────────────────────────────────────────────────────────
   Discovery
   ──────────────────────────────────────────────────────────── */

const modules = import.meta.glob<GeneratedGuideDocument>('./generated/*.json', {
  eager: true,
  import: 'default',
});

function isPublishable(doc: GeneratedGuideDocument): boolean {
  return doc?.schemaVersion === 1 && doc.status === 'published' && Boolean(doc.slug);
}

/** Level-2 headings become the table of contents — no separately-drifting list. */
function tocFromBlocks(blocks: GuideBlock[]) {
  const items = blocks
    .filter((b): b is Extract<GuideBlock, { type: 'heading' }> => b.type === 'heading')
    .filter((b) => b.level === 2)
    .map((b) => ({ id: b.id, label: b.text, labelKo: b.textKo }));
  return [...items, { id: 'faq', label: 'FAQ', labelKo: '자주 묻는 질문' }];
}

export const GENERATED_DOCS: GeneratedGuideDocument[] = Object.values(modules)
  .filter(Boolean)
  .filter(isPublishable)
  .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));

/** Every generated doc including drafts — for tooling and the draft preview. */
export const ALL_GENERATED_DOCS: GeneratedGuideDocument[] = Object.values(modules).filter(Boolean);

export function toGuideMeta(doc: GeneratedGuideDocument): GuideMeta {
  return {
    slug: doc.slug,
    category: doc.category,
    title: doc.title,
    titleKo: doc.titleKo,
    shortTitle: doc.shortTitle,
    description: doc.description,
    excerpt: doc.excerpt,
    readingTimeMinutes: doc.readingTimeMinutes,
    publishedAt: doc.publishedAt,
    updatedAt: doc.updatedAt,
    lastVerifiedAt: doc.lastVerifiedAt,
    authorId: doc.authorId,
    factChecked: doc.factChecked,
    toc: tocFromBlocks(doc.blocks),
    faq: doc.faq,
    sources: doc.sources,
    keywords: doc.keywords,
    disclaimer: doc.disclaimer,
    gradient: doc.gradient,
    related: doc.related,
  };
}

export const GENERATED_GUIDES: GuideMeta[] = GENERATED_DOCS.map(toGuideMeta);

const docsBySlug = new Map(ALL_GENERATED_DOCS.map((d) => [d.slug, d]));

export function getGeneratedDoc(slug: string): GeneratedGuideDocument | undefined {
  const doc = docsBySlug.get(slug);
  return doc && doc.status === 'published' ? doc : undefined;
}

export function hasGeneratedGuide(slug: string): boolean {
  return getGeneratedDoc(slug) !== undefined;
}
